/**
 * Shared axios instance for talking to Strapi.
 *
 * Security audit follow-up: the codebase used to ship `REACT_APP_API_KEY`
 * (and worse, `REACT_APP_ROOT_KEY`) baked into the bundle, which meant any
 * visitor could read those tokens out of the JS and call the entire API.
 *
 * This module replaces that pattern with a per-user JWT obtained from
 * `/auth/local` at login and stored under localStorage["token"]. The
 * interceptor:
 *   - Attaches `Authorization: Bearer <jwt>` to every outgoing request,
 *     UNLESS the caller already set an Authorization header (so existing
 *     legacy call sites that still pass REACT_APP_API_KEY keep working
 *     during the incremental migration).
 *   - On any 401 response, clears the stored credentials and bounces the
 *     user back to /login so a revoked / expired token can't get stuck.
 *
 * Usage:
 *   import api from "../lib/axios";
 *   const res = await api.get("users?filters[email][$eq]=...");
 *
 * The baseURL is REACT_APP_API_URL — pass relative paths only.
 */

import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 30000,
});

api.interceptors.request.use(
  (config) => {
    // Don't overwrite an Authorization header the caller already set —
    // some existing call sites still need to pass a specific token.
    const hasAuthHeader =
      config.headers &&
      (config.headers.Authorization || config.headers.authorization);
    if (!hasAuthHeader) {
      const jwt = localStorage.getItem("token");
      if (jwt) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${jwt}`;
      }
    }
    return config;
  },
  (err) => Promise.reject(err)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error && error.response && error.response.status;
    if (status === 401) {
      // Stale / revoked JWT — clear and force re-login. Skip the redirect
      // when we're already on /login to avoid loops.
      try {
        localStorage.removeItem("token");
        localStorage.removeItem("userData");
        localStorage.removeItem("userid");
      } catch (_) {}
      if (
        typeof window !== "undefined" &&
        window.location &&
        !/\/login$/.test(window.location.pathname)
      ) {
        window.location.assign("/login");
      }
    }
    return Promise.reject(error);
  }
);

export default api;
